import { Crown, Skull } from "lucide-react"
import StarIcon from "@mui/icons-material/Star"
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard"
import React from "react"
import Box from "@mui/material/Box"
import { DialogActions, DialogContent, DialogTitle } from "@mui/material"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import WavesIcon from "@mui/icons-material/Waves"
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined"
import Button from "@mui/material/Button"
import { NavLink } from "react-router-dom"
import { PATHS } from "../../../../constants/paths"

const RESULT_CONFIG = {
  complete: {
    title: "RUN COMPLETE",
    titleIcon: null,
    noticeIcon: <Skull />,
    noticeTitle: "CURRENT SCORE",
    noticeText: "You did not beat your personal best.",
  },
  personalBest: {
    title: "NEW PERSONAL BEST!",
    titleIcon: null,
    noticeIcon: <StarIcon />,
    noticeTitle: "NEW PERSONAL BEST!",
    noticeText: "You improved your personal best score!",
  },
  prize: {
    title: "PRIZE POSITION REACHED!",
    titleIcon: <EmojiEventsIcon />,
    noticeIcon: <CardGiftcardIcon />,
    noticeTitle: "YOU ARE CURRENTLY IN A REWARDED POSITION!",
    noticeText: "Keep improving your score to increase your reward.",
  },
}
const StatRow = ({ icon, label, value, highlight }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "44px minmax(150px, 1fr) 150px",
      alignItems: "end",
      minHeight: "43px",
      color: "#d8d8d8",
      textAlign: "left",
    }}
  >
    <Box
      sx={{
        color: "#cfcfcf",

        "& svg": {
          width: "28px",
          height: "28px",
          color: "#e5e5e5",
        },

        "& span": {
          fontSize: "15px",
          fontWeight: "900",
        },
      }}
    >
      {icon}
    </Box>

    <Typography
      component="span"
      sx={{
        color: "#cfcfcf",
        borderBottom: "1px solid rgba(198,146,76,0.14)",
      }}
    >
      {label}
    </Typography>

    <Typography
      component="span"
      sx={{
        color: highlight ? "#c49654" : "",
        borderBottom: "1px solid rgba(198,146,76,0.14)",
        textAlign: "right",
        fontSize: 20,
        fontWeight: 600,
      }}
    >
      {value}
    </Typography>
  </Box>
)

// Функция для форматирования секунд
const formatTime = (totalSeconds) => {
  if (totalSeconds < 0 || isNaN(totalSeconds)) return '0m 0s';

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}m ${seconds}s`;
};

export const TournamentResultDialog = ({
  closeDialog,
  playAgain,
  title,
  zombiesKilled = 0,
  waveReached = 0,
  bossesKilled = 0,
  score = 0,
  bestScore = 0,
  timePlayed = 0,
  position,
  totalPlayers,
  players,
  variant: variantProp,
  width = 500,
}) => {
  // totalPlayers — основной проп; players оставлен для обратной совместимости
  const playersCount = totalPlayers ?? players

  let variant = variantProp || "complete"
  const isPersonalBest = bestScore === score

  if (!variantProp) {
    if (isPersonalBest) {
      variant = "personalBest"
    }

    if (position < 4) {
      variant = "prize"
    }
  }

  const config = RESULT_CONFIG[variant] || RESULT_CONFIG.complete
  const isComplete = variant === "complete"

  return (
    <Box
      sx={{
        width,
        textAlign: "center",
        border: "1px solid rgba(198, 146, 76, 0.25)",
        borderRadius: 1,
      }}
    >
      <DialogTitle sx={{ fontSize: "1rem", position: "relative" }}>
        <Typography sx={{ textTransform: "uppercase", pr: 5 }} variant="h1">
          {config.title}
        </Typography>

        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: "absolute",
            top: 5,
            right: 5,
            color: "#c7954f",
            "&:hover": {
              background: "rgba(214,154,73,0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>

        <Typography
          sx={{
            background: "#dedede",
            backgroundClip: "text",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 0, fontSize: "16px !important" }}>
        {/* Statistic rows */}
        <Box sx={{ px: 2 }}>
          <StatRow
            icon={<Skull />}
            label="ZOMBIES KILLED"
            value={zombiesKilled.toLocaleString("en-US")}
            highlight={isPersonalBest}
          />
          <StatRow
            icon={<WavesIcon />}
            label="WAVE REACHED"
            value={waveReached.toLocaleString("en-US")}
            highlight={isPersonalBest}
          />
          <StatRow
            icon={<Crown />}
            label="BOSSES KILLED"
            value={bossesKilled.toLocaleString("en-US")}
            highlight={isPersonalBest}
          />
          <StatRow
            icon={<TimerOutlinedIcon />}
            label="SCORE"
            value={score.toLocaleString("en-US")}
            highlight={isPersonalBest}
          />
          <StatRow
            icon={<TimerOutlinedIcon />}
            label="TIME PLAYED"
            value={formatTime(timePlayed)}
            highlight={isPersonalBest}
          />
        </Box>
        {/* end of Statistic rows */}

        {/* Position & Score */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1px 1fr",
            gap: 0,
            mt: 3,
            mb: 2,
            py: 2.25,
            border: "1px solid rgba(198, 146, 76, 0.25)",
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              minHeight: 90,
            }}
          >
            <Typography
              sx={{
                mb: 1,
                color: "#dedede",
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: 0.7,
              }}
            >
              YOUR POSITION
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                gap: 1, // 8px in theme

                "& span": {
                  color: "#c7954f",
                  fontSize: 42,
                  lineHeight: 1,
                  fontWeight: 900,
                },

                "& small": {
                  color: "#e5e5e5",
                  fontSize: 18,
                  fontWeight: 700,
                },
              }}
            >
              <Typography component="span">#{position}</Typography>

              <Typography component="small">/ {playersCount}</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: "1px",
              background: "rgba(198, 146, 76, 0.25)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: 90,
            }}
          >
            <Typography
              sx={{
                mb: 1,
                color: "#dedede",
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: 0.7,
              }}
            >
              YOUR BEST SCORE
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                "& svg": {
                  width: 31,
                  height: 31,
                  color: "#c7954f",
                },
                "& span": {
                  color: "#dedede",
                  fontSize: 36,
                  lineHeight: 1,
                  fontWeight: 800,
                },
              }}
            >
              <EmojiEventsIcon />
              <Typography component="span">
                {bestScore.toLocaleString("en-US")}
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 1,
                color: "#dedede",
                fontSize: 16,
              }}
            >
              {isPersonalBest ? "New personal best" : "Your personal best"}
            </Typography>
          </Box>
        </Box>
        {/* end of Position & Score */}

        {/* Info box */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isComplete ? "110px 1fr" : "70px 1fr",
            alignItems: "center",
            minHeight: 112,
            p: "15px 22px",
            border: isComplete
              ? "1px solid rgba(198, 146, 76, 0.25)"
              : "1px solid rgba(255,193,62,0.35)",
            backgroundColor: isComplete ? "inherit" : "rgba(255,193,62,0.02)",
            borderRadius: "5px",
            textAlign: "left",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pr: 3,

              "& svg": {
                color: isComplete ? "#999" : "#c7954f",
                width: isComplete ? 100 : 50,
                height: isComplete ? 100 : 50,
              },
            }}
          >
            {config.noticeIcon}
          </Box>

          <Box>
            <Typography
              sx={{
                color: isComplete ? "#aaa" : "#c7954f",
                fontSize: 16,
                fontWeight: 900,
              }}
            >
              {config.noticeTitle}
            </Typography>

            {variant === "complete" && (
              <Typography
                sx={{
                  color: "#dedede",
                  fontSize: 36,
                  fontWeight: 800,
                }}
              >
                {score.toLocaleString("en-US")}
              </Typography>
            )}

            <Typography
              sx={{
                color: "#aaa",
                fontSize: 16,
              }}
            >
              {config.noticeText}
            </Typography>
          </Box>
        </Box>
        {/* end of Info box */}
      </DialogContent>

      <DialogActions
        sx={{ p: 3, width: "100%", display: "flex", flexDirection: "column" }}
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={playAgain}
          sx={{
            background: "linear-gradient(135deg, #FF6B00, #C13D00)",
            mt: 2,
            zIndex: 0,
            fontSize: 22,
            fontWeight: 400,
            "&::before": {
              content: '""',
              borderRadius: "inherit",
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgb(230,85,0), rgb(160,45,0))",
              opacity: 0,
              transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: -1,
            },
            "&:hover::before": {
              opacity: 1,
            },
          }}
        >
          Play again
        </Button>

        <Button
          variant="contained"
          size="large"
          fullWidth
          component={NavLink}
          to={`${PATHS.TOURNAMENTS.PATH}`}
          onClick={closeDialog}
          sx={{
            background: "linear-gradient(135deg, #191919, #101010)",
            mt: 2,
            ml: "0 !important",
            zIndex: 0,
            fontSize: 22,
            fontWeight: 400,
            "&::before": {
              content: '""',
              borderRadius: "inherit",
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #242424, #151515)",
              opacity: 0,
              transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: -1,
            },

            "&:hover::before": {
              opacity: 1,
            },
          }}
        >
          Back to tournaments
        </Button>
      </DialogActions>
    </Box>
  )
}
